import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Plus, Trash2, Newspaper, Tag } from "lucide-react";
import { toast } from "sonner";
import UpcomingWidget from "./UpcomingWidget";

export default function News({ isAdmin }) {
  const [news, setNews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tag: "", image_url: "" });
  const [q, setQ] = useState("");

  const load = () => api.get("/news").then(r => setNews(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) return;
    try {
      await api.post("/news", form);
      setForm({ title: "", body: "", tag: "", image_url: "" });
      setShowForm(false);
      toast.success("News pubblicata");
      load();
    } catch { toast.error("Errore nella pubblicazione"); }
  };

  const remove = async (id) => {
    await api.delete(`/news/${id}`);
    toast.success("Eliminata");
    load();
  };

  const filtered = news.filter(n => !q || n.title.toLowerCase().includes(q.toLowerCase()) || (n.tag || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="section-news">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><Newspaper className="w-3 h-3" /> Sito & Comunicazioni</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">News dell'Associazione</h1>
          <p className="text-[#6B6558] mt-2">Tutti gli aggiornamenti, gli eventi e gli annunci dell&apos;Associazione.</p>
        </div>
        {isAdmin && (
          <button data-testid="new-news-btn" onClick={() => setShowForm(v => !v)} className="terracotta-btn px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold">
            <Plus className="w-4 h-4" /> Nuova News
          </button>
        )}
      </div>

      <input
        data-testid="news-search"
        placeholder="Cerca per titolo o tag…"
        value={q}
        onChange={e => setQ(e.target.value)}
        className="w-full mb-6 px-4 py-3 rounded-xl border border-[#E8E2D8] bg-white focus:outline-none focus:border-[#EF3E36]"
      />

      <UpcomingWidget onOpenCalendar={() => window.dispatchEvent(new CustomEvent("amuni:go-tab", { detail: { tab: "calendar" } }))} />

      {isAdmin && showForm && (
        <form onSubmit={submit} className="paper p-6 rounded-2xl mb-6 space-y-3" data-testid="news-form">
          <input required data-testid="news-title-input" placeholder="Titolo" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <textarea required data-testid="news-body-input" placeholder="Testo…" rows={4} value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="news-tag-input" placeholder="Tag (es. Evento, Bando)" value={form.tag} onChange={e => setForm(f => ({...f, tag: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <input data-testid="news-image-input" placeholder="URL immagine (opzionale)" value={form.image_url} onChange={e => setForm(f => ({...f, image_url: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="news-submit-btn" className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold">Pubblica</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Annulla</button>
          </div>
        </form>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {filtered.length === 0 && (
          <div className="paper p-8 rounded-2xl col-span-full text-center text-[#6B6558]">
            Nessuna news pubblicata.
          </div>
        )}
        {filtered.map(n => (
          <article key={n.id} data-testid={`news-card-${n.id}`} className="paper rounded-2xl overflow-hidden card-hover">
            {n.image_url && <img src={n.image_url} alt="" className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-xl font-bold text-[#2B3A33]">{n.title}</h3>
                {isAdmin && (
                  <button data-testid={`delete-news-${n.id}`} onClick={() => remove(n.id)} className="text-[#EF3E36] p-1 hover:bg-[#FDF2F2] rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-[#6B6558] text-sm mt-2 leading-relaxed line-clamp-4">{n.body}</p>
              <div className="mt-4 flex items-center gap-3 text-xs text-[#8A8272]">
                {n.tag && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] font-medium"><Tag className="w-3 h-3" />{n.tag}</span>}
                <span>{formatDate(n.created_at)}</span>
                {n.author_name && <span>· {n.author_name}</span>}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
